import {
  ListBlockModel,
  ParagraphBlockModel,
  ParagraphBlockSchema,
  type ParagraphType,
} from '@blocksuite/affine-model';
import { focusTextModel } from '@blocksuite/affine-rich-text';
import type { AffineTextAttributes } from '@blocksuite/affine-shared/types';
import { matchModels } from '@blocksuite/affine-shared/utils';
import type { BlockComponent } from '@blocksuite/std';
import {
  InlineMarkdownExtension,
  type InlineMarkdownMatchContext,
} from '@blocksuite/std/inline';

function getParagraphMarkdownTarget({
  inlineEditor,
  inlineRange,
  pattern,
  prefixText,
}: InlineMarkdownMatchContext<AffineTextAttributes>) {
  if (inlineEditor.yTextString.slice(0, inlineRange.index).includes('\n')) {
    return null;
  }

  const match = prefixText.match(pattern);
  if (!match) return null;

  const type = (match[2] ? `h${match[2].length}` : 'quote') as ParagraphType;

  if (!inlineEditor.rootElement) return null;
  const blockComponent =
    inlineEditor.rootElement.closest<BlockComponent>('[data-block-id]');
  if (!blockComponent) return null;

  const { model } = blockComponent;
  if (matchModels(model, [ListBlockModel])) {
    return { blockComponent, type };
  }
  if (matchModels(model, [ParagraphBlockModel]) && model.props.type !== type) {
    return { blockComponent, type };
  }
  return null;
}

export const ParagraphMarkdownExtension =
  InlineMarkdownExtension<AffineTextAttributes>({
    name: 'heading',
    completionHint: {
      type: 'heading-or-quote',
      isEligible: props => getParagraphMarkdownTarget(props) !== null,
      getTarget: props =>
        getParagraphMarkdownTarget(props)?.type ?? 'heading-or-quote',
    },
    pattern: /^((#{1,6})|(>))\s$/,
    action: props => {
      const target = getParagraphMarkdownTarget(props);
      if (!target) return;

      const { inlineEditor, inlineRange } = props;
      const { blockComponent, type } = target;
      const { model, std, store } = blockComponent;
      if (matchModels(model, [ListBlockModel])) {
        const parent = store.getParent(model);
        if (!parent) return;
        const index = parent.children.indexOf(model);

        store.captureSync();
        inlineEditor.deleteText({
          index: 0,
          length: inlineRange.index,
        });
        store.deleteBlock(model, { deleteChildren: false });
        const id = store.addBlock<ParagraphBlockModel>(
          ParagraphBlockSchema.model.flavour,
          {
            type: type,
            text: model.text?.clone(),
            children: model.children,
          },
          parent,
          index
        );

        focusTextModel(std, id);
      } else if (matchModels(model, [ParagraphBlockModel])) {
        store.captureSync();
        inlineEditor.deleteText({
          index: 0,
          length: inlineRange.index,
        });
        store.updateBlock(model, { type });
        focusTextModel(std, model.id);
      }
    },
  });
